<template>
    <div>
        <v-row>
            <div
                class="d-flex mb-6 mt-3"
                v-for="(insight, index) of reactiveInsights"
                :key="index"
            >
                <v-card
                    class="pt-5 px-2 ma-2"
                    min-width="250px"
                    max-width="250px"
                >
                    <v-card-text
                        class="d-flex my-0 py-0 mx-0 px-0"
                        width="100%"
                    >
                        <v-chip
                            class="mb-1 mt-0 mx-2 pt-0 white--text"
                            :color="insight.color"
                            label
                            :style="{ height: '50px' }"
                        >
                            <v-icon center>
                                {{ insight.icon }}
                            </v-icon>
                        </v-chip>
                        <div class="mb-1 mt-0 pt-0">
                            <div class="d-flex mr-0 pr-0 text-capitalize">
                                <span>{{ insight.title }}</span>
                            </div>
                            <p class="text-h6 black--text">
                                {{ insight.value }}
                            </p>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </v-row>
        <v-data-table
            :headers="scopeHeaders"
            :items="sortedScopes"
            class="elevation-0 mb-3"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4 align-center"
                    style="background-color: #eeeeee; display: flex; flex-wrap: wrap;"
                >
                    <v-col cols="9">
                        <div class="d-flex align-center">
                            <h2 class="ml-4">SCOPES</h2>
                            <v-spacer />
                            <v-btn
                                v-if="
                                    canEditForecast && selectedScopes.length > 0
                                "
                                color="primary"
                                @click="openBulkForecastDialog"
                                class="mr-2"
                                small
                            >
                                <v-icon left>mdi-plus</v-icon>
                                Bulk Forecast ({{ selectedScopes.length }})
                            </v-btn>
                        </div>
                    </v-col>

                    <v-col cols="3" style="min-width: fit-content;">
                        <v-btn-toggle
                            :style="{ width: '100%' }"
                            v-if="!scopeInEdition"
                        >
                            <v-btn
                                @click.stop="showYears()"
                                rounded
                                small
                                :style="{ width: '50%' }"
                                >YEARS</v-btn
                            >
                            <v-btn
                                @click.stop="showMonths()"
                                rounded
                                small
                                :style="{ width: '50%' }"
                                >MONTHS</v-btn
                            >
                        </v-btn-toggle>
                    </v-col>
                </v-row>
            </template>
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="py-2 mt-2 custom-label"
                    :label="header.text"
                    v-model="scopeSearchFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.select`]>
                <v-checkbox
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    hide-details
                    class="ma-0 pa-0"
                    :indeterminate="isIndeterminate"
                />
            </template>
            <template v-slot:[`item.select`]="{ item }">
                <v-checkbox
                    v-if="item.id != 'free'"
                    v-model="selectedScopes"
                    :value="item.id"
                    hide-details
                    class="ma-0 pa-0"
                />
            </template>
            <template v-slot:[`item.totalPrice`]="{ item }">
                <div class="my-4">
                    <p>
                        {{ formatCurrency(project.currency, item.totalPrice) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.currentBalanceToBill`]="{ item }">
                <div class="my-4">
                    <p>
                        {{
                            formatCurrency(
                                project.currency,
                                item.currentBalanceToBill
                            )
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.baseBalanceToBill`]="{ item }">
                <div class="my-4">
                    <p>
                        {{
                            formatCurrency(
                                project.currency,
                                item.baseBalanceToBill
                            )
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.actualVsForecasted`]="{ item }">
                <div class="my-4">
                    <p>{{ (item.actualVsForecasted || 0).toFixed(2) }} %</p>
                </div>
            </template>
            <template v-slot:[`item.invoicedThisMonth`]="{ item }">
                <div class="my-4">
                    <p class="d-flex justify-end">
                        {{
                            item.invoicedThisMonth
                                ? formatCurrency(
                                      project.currency,
                                      +item.invoicedThisMonth
                                  )
                                : '-'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.dateYear`]="{ item, header }">
                <p class="d-flex justify-end align-right my-1">
                    {{ getDateScopePercentage(item, header.year) }}
                    %
                </p>
                <p class="d-flex justify-end align-right mb-1">
                    {{
                        formatCurrency(
                            project.currency,
                            getScopeDateAmount(item, header.year)
                        )
                    }}
                </p>
            </template>
            <template v-slot:[`item.totalInvoiced`]="{ item }">
                <p class="d-flex justify-end align-right mb-1">
                    {{ formatCurrency(project.currency, item.totalInvoiced) }}
                </p>
            </template>
            <template v-slot:[`item.invoicedPercentage`]="{ item }">
                <div class="my-4">
                    <p
                        class="my-0 text-capitalize"
                        :class="{
                            'text-orange': item.invoicedPercentage < 100,
                            'text-green': item.invoicedPercentage == 100,
                            'text-red': item.invoicedPercentage > 100,
                        }"
                    >
                        {{ item.invoicedPercentage || 0 }}%
                    </p>
                </div>
            </template>
            <template v-slot:[`item.forecastedPercentage`]="{ item }">
                <div class="my-4">
                    <p
                        class="my-0 text-capitalize"
                        :class="{
                            'text-orange': item.forecastedPercentage < 100,
                            'text-green': item.forecastedPercentage == 100,
                            'text-red': item.forecastedPercentage > 100,
                        }"
                    >
                        {{ item.forecastedPercentage || 0 }}%
                    </p>
                </div>
            </template>
            <template v-slot:[`item.date`]="{ item, header }">
                <v-col>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-row class="d-flex justify-end align-right">
                                    <p class="my-1">
                                        {{
                                            getDateScopePercentage(
                                                item,
                                                header.month
                                            ) == '0.00' ||
                                            getDateScopePercentage(
                                                item,
                                                header.month
                                            ) == 0
                                                ? '-'
                                                : getDateScopePercentage(
                                                      item,
                                                      header.month
                                                  ) + '%'
                                        }}
                                    </p>
                                    <v-tooltip
                                        right
                                        class="d-flex justify-end align-right ml-1"
                                        v-if="
                                            scopeInEdition == item.id &&
                                                !deletionAction
                                        "
                                    >
                                        <template
                                            v-slot:activator="{
                                                on: onEdit,
                                                attrs: attrsEdit,
                                            }"
                                        >
                                            <v-btn
                                                small
                                                icon
                                                class="d-flex justify-end align-right mt-0 ml-0"
                                                v-bind="attrsEdit"
                                                v-on="onEdit"
                                                v-if="!deletionAction"
                                                @click="
                                                    openEditScopeMonth(
                                                        item[header.month],
                                                        header.month
                                                    )
                                                "
                                            >
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edit Forecast</span>
                                    </v-tooltip>
                                </v-row>
                                <v-row class="d-flex justify-end align-right">
                                    <p class="mb-1">
                                        {{
                                            getScopeDateAmount(
                                                item,
                                                header.month
                                            ) == 0
                                                ? '-'
                                                : formatCurrency(
                                                      project.currency,
                                                      getScopeDateAmount(
                                                          item,
                                                          header.month
                                                      )
                                                  )
                                        }}
                                    </p>
                                </v-row>
                            </span>
                        </template>
                        <div class="d-flex flex-column">
                            <div
                                v-if="getMonthReferenceInfo(item, header.month)"
                                class="mb-2"
                            >
                                <strong>Reference: </strong>
                                <span>
                                    {{
                                        formatCurrency(
                                            project.currency,
                                            getMonthReferenceInfo(
                                                item,
                                                header.month
                                            ).amount || 0
                                        )
                                    }}
                                    ({{
                                        parseFloat(
                                            getMonthReferenceInfo(
                                                item,
                                                header.month
                                            ).percentage || 0
                                        ).toFixed(2)
                                    }}%)
                                </span>
                            </div>
                            <div v-else class="mb-2">
                                <strong>Reference: </strong>
                                <span>No data available</span>
                            </div>
                            <div
                                v-if="getMonthForecastInfo(item, header.month)"
                            >
                                <strong>Forecast: </strong>
                                <span>
                                    {{
                                        formatCurrency(
                                            project.currency,
                                            getMonthForecastInfo(
                                                item,
                                                header.month
                                            ).amount || 0
                                        )
                                    }}
                                    ({{
                                        parseFloat(
                                            getMonthForecastInfo(
                                                item,
                                                header.month
                                            ).percentage || 0
                                        ).toFixed(2)
                                    }}%)
                                </span>
                            </div>
                            <div v-else>
                                <strong>Forecast: </strong>
                                <span>No data available</span>
                            </div>
                        </div>
                    </v-tooltip>
                </v-col>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <td
                    :class="['fixed-column', 'white-background']"
                    :style="{
                        backgroundColor: 'white',
                        width: '150px',
                        textAlign: 'center',
                    }"
                    v-if="item"
                >
                    <v-menu rounded offset-y :disabled="item.id == 'free'">
                        <template v-slot:activator="{ attrs, on }">
                            <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                                small
                                depressed
                                class="ml-2"
                                :disabled="
                                    loading ||
                                        (scopeInEdition &&
                                            item.id != scopeInEdition)
                                "
                            >
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                v-if="
                                    item.id != scopeInEdition && canEditForecast
                                "
                                @click.stop="openReplaceScopeForecast(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1">mdi-sync</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Replace Quote Forecast
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="
                                    item.id != scopeInEdition &&
                                        user.permissions &&
                                        user.permissions.includes(
                                            'editInitialProjection'
                                        )
                                "
                                @click.stop="
                                    openEditInitialProjectionForecast(item)
                                "
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1"
                                        >mdi-pencil-outline</v-icon
                                    >
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Edit Initial Projection
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="item.id != scopeInEdition"
                                @click.stop="openHistory(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1">mdi-book</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Forecast History
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="
                                    item.id == scopeInEdition && !deletionAction
                                "
                                @click="openAddNewScopeMonth()"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1"
                                        >mdi-plus-circle</v-icon
                                    >
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Add New Month
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="
                                    item.id == scopeInEdition && !deletionAction
                                "
                                @click="openDeleteScopeMonths(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1">mdi-delete</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Delete Forecast
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="item.id == scopeInEdition"
                                :disabled="
                                    item.forecastedPercentage > 100 &&
                                        !isEditingInitialProjection &&
                                        !deletionAction
                                "
                                @click="replaceScopeForecast()"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1"
                                        >mdi-content-save</v-icon
                                    >
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{
                                        isEditingInitialProjection
                                            ? 'Update Initial Projection'
                                            : 'Update Forecast'
                                    }}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="item.id == scopeInEdition"
                                @click="closeScopeReplaceForecast()"
                            >
                                <v-list-item-icon>
                                    <v-icon class="mr-1"
                                        >mdi-close-thick</v-icon
                                    >
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Cancel Update
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </td>
            </template>
            <template v-slot:[`body.append`]>
                <tr :style="{ 'background-color': '#eeeeee' }">
                    <!-- Select column -->
                    <td class="font-weight-bold"></td>
                    <!-- SCOPE column -->
                    <td class="font-weight-bold">TOTALS</td>
                    <!-- QUOTE column -->
                    <td class="font-weight-bold"></td>
                    <!-- Total Price -->
                    <td class="font-weight-bold">
                        <p class="d-flex justify-end align-right my-0">
                            {{
                                formatCurrency(
                                    project.currency,
                                    totalScopePrice
                                )
                            }}
                        </p>
                    </td>
                    <!-- CURRENT B2B -->
                    <td class="font-weight-bold">
                        <p
                            class="d-flex justify-end align-right my-0"
                            :style="{ width: '100%' }"
                        >
                            {{
                                formatCurrency(
                                    project.currency,
                                    totalCurrentB2B
                                )
                            }}
                        </p>
                    </td>
                    <!-- BASE B2B -->
                    <td class="font-weight-bold">
                        <p
                            class="d-flex d-flex justify-end align-right my-0"
                            :style="{ width: '100%' }"
                        >
                            {{ formatCurrency(project.currency, totalBaseB2B) }}
                        </p>
                    </td>
                    <td class="font-weight-bold"></td>
                    <!-- Invoiced in current month -->
                    <td class="font-weight-bold text-end">
                        {{ totalInvoicedPerDate }}
                    </td>
                    <!-- Total invoiced -->
                    <td class="font-weight-bold text-end">
                        {{ formatCurrency(project.currency, totalInvoiced) }}
                    </td>
                    <!-- Invoiced Percentage -->
                    <td class="font-weight-bold text-end">
                        {{ invoicedTotalPercentage }}%
                    </td>
                    <!-- Forecasted Percentage -->
                    <td class="font-weight-bold text-end">
                        {{ reactiveInsights[3].value }}
                    </td>
                    <!-- Forecasts per month -->
                    <template v-if="viewType == 'month'">
                        <td
                            class="my-0"
                            v-for="(header, index) in scopeHeaders.filter(
                                h => h.type === 'month' && h.value === 'date'
                            )"
                            :key="index"
                        >
                            <p
                                class="d-flex justify-end align-right my-0"
                                v-if="viewType == 'month'"
                            >
                                {{
                                    parseCurrency(
                                        totalsPerScopeDate[header.month] ||
                                            '0.00% - $0.00'
                                    )[0]
                                }}
                            </p>
                            <p
                                class="d-flex justify-end align-right my-0"
                                v-if="viewType == 'month'"
                            >
                                {{
                                    parseCurrency(
                                        totalsPerScopeDate[header.month] ||
                                            '0.00% - $0.00'
                                    )[1]
                                }}
                            </p>
                        </td>
                    </template>
                    <!-- Forecasts per year -->
                    <template v-if="viewType == 'year'">
                        <td
                            class="my-0"
                            v-for="(header, index) in scopeHeaders.filter(
                                h => h.type === 'year' && h.value === 'dateYear'
                            )"
                            :key="index"
                        >
                            <p
                                class="d-flex justify-end align-right my-0"
                                v-if="viewType == 'year'"
                            >
                                {{
                                    parseCurrency(
                                        totalsPerScopeDate[header.text] ||
                                            '0.00% - $0.00'
                                    )[0]
                                }}
                            </p>
                            <p
                                class="d-flex justify-end align-right my-0"
                                v-if="viewType == 'year'"
                            >
                                {{
                                    parseCurrency(
                                        totalsPerScopeDate[header.text] ||
                                            '0.00% - $0.00'
                                    )[1]
                                }}
                            </p>
                        </td>
                    </template>
                    <td :class="['fixed-column', 'gray-background']" />
                </tr>
            </template>
        </v-data-table>
        <!-- Delete ALL Months -->
        <v-dialog
            v-model="deleteScopeMonthsDialog"
            :retain-focus="false"
            persistent
            max-width="400"
        >
            <v-card :loading="loading">
                <v-card-title class="text-h5">
                    <v-btn
                        small
                        icon
                        @click="closeDeleteScopesMonthsDialog"
                        color="primary"
                        class="mr-2"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                    <h3>
                        Delete all forecasts
                    </h3>
                </v-card-title>
                <v-card-text>
                    Are you sure you want to delete forecasts for all months?
                </v-card-text>
                <v-card-actions class="pb-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteScopeMonths()"
                        color="error"
                        :loading="loading"
                        :disabled="deletionAction || loading"
                        rounded
                    >
                        DELETE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--ForecastHistory-->
        <v-dialog persistent max-width="2000px" v-model="historyView">
            <ForecastHistory
                v-if="historyView"
                :historyQuote="historyQuote"
                :project="project"
                :invoices="invoices"
                @closeDialog="closeHistoryView"
            />
        </v-dialog>
        <!-- TEST SCOFE FORECAST FORM -->
        <v-dialog persistent max-width="300px" v-model="newMonthScopeForm">
            <ForecastEntryForm
                v-if="newMonthScopeForm"
                :createDialog="createDialog"
                :originalEntry="originalEntry"
                :dates="dateToShow"
                :forecast="forecast"
                :scope="selectedScope"
                @addEntry="addEntry"
                @replaceEntry="replaceEntry"
                @closeDialog="closeDialog"
            />
        </v-dialog>

        <!-- Bulk Forecast Dialog -->
        <BulkForecastDialog
            :dialog="bulkForecastDialog"
            :selectedScopes="selectedScopes"
            :scopes="scopes"
            :project="project"
            :forecastOrigin="forecast"
            :dates="dateToShow"
            @closeBulkForecastDialog="closeBulkForecastDialog"
            @onBulkForecastCreated="onBulkForecastCreated"
            @removeScopeFromSelection="removeScopeFromSelection"
        />
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import API from '@/services/api'
import { objDiff } from '@/helpers/objDiff'

export default {
    name: 'B2BForecast',
    props: {
        projectId: {
            type: String,
            default: () => '',
        },
    },
    components: {
        ForecastHistory: () =>
            import('@/components/Forecast/ForecastHistory.vue'),
        ForecastEntryForm: () =>
            import('@/components/Forecast/ForecastEntryForm.vue'),
        BulkForecastDialog: () =>
            import('@/components/Forecast/BulkForecastDialog.vue'),
    },
    data: () => ({
        loading: true,
        companyId: JSON.parse(localStorage.getItem('company')),
        quoteHeaders: [],
        months: [
            'JAN',
            'FEB',
            'MAR',
            'APR',
            'MAY',
            'JUN',
            'JUL',
            'AUG',
            'SEP',
            'OCT',
            'NOV',
            'DEC',
        ],
        viewType: 'month',
        forecastDialog: false,
        createDialog: undefined,
        selectedForecast: {},
        dateToShow: [],
        forecast: {},
        quoteInEdition: undefined,
        originalEntry: {},
        quotes: [],
        invoices: {},
        invoicedPerQuote: {},
        originalForecast: null,
        originalForecast2: null,
        currentDate: '',
        project: {},
        historyView: false,
        historyQuote: {},
        historyInvoices: null,
        allDates: [],
        user: {},
        originalHeaders: [],
        scopes: [],
        editScopeForecast: false,
        newMonthScopeForm: false,
        scopeInEdition: undefined,
        selectedScopeForecast: {},
        selectedScope: {},
        isEditingInitialProjection: false,
        invoicedPerScope: {},
        scopeHeaders: [],
        initialInvoiced: 0,
        costs: {},
        deleteScopeMonthsDialog: false,
        deletionAction: false,
        scopeSearchFilter: '',
        selectedScopes: [],
        bulkForecastDialog: false,
        selectAll: false,
        totalcredits: 0,
    }),
    computed: {
        canEditForecast() {
            if (!this.user?.id || !this.project) return false

            if (this.user.id === this.project.projectManager) return true

            const isProjectManagerRole =
                this.user.role &&
                this.user.role.toLowerCase() === 'project manager'
            const isCollaborator = this.project.collaborators?.includes(
                this.user.id
            )

            return isProjectManagerRole && isCollaborator
        },
        scopeForecastDiff: function() {
            const forecastKey = Object.keys(this.forecast.scopes).find(
                key => this.forecast.scopes[key].id == this.scopeInEdition
            )

            const newForecast = this.forecast.scopes[forecastKey]

            if (newForecast) {
                return objDiff(this.selectedScopeForecast, newForecast)
            } else {
                return []
            }
        },
        totalInvoiced() {
            return this.scopes.reduce(
                (acc, scope) => acc + (scope.totalInvoiced || 0),
                0
            )
        },
        processedScopes() {
            return this.scopes.map(scope => {
                const invoicedPercentage = this.calculateScopeInvoicedPercentage(
                    scope
                )
                const forecastedPercentage = this.calculateScopeForecastedPercentage(
                    scope
                )

                return {
                    ...scope,
                    invoicedPercentage: parseFloat(invoicedPercentage),
                    forecastedPercentage: parseFloat(forecastedPercentage),
                    isFullyInvoiced: parseFloat(invoicedPercentage) >= 100,
                }
            })
        },
        sortedScopes() {
            // Use processedScopes computed property for reactivity
            let scopes = this.processedScopes

            // Sort scopes: 100% invoiced at the end
            scopes = [...scopes].sort((a, b) => {
                if (a.isFullyInvoiced && b.isFullyInvoiced) return 0
                if (a.isFullyInvoiced) return 1
                if (b.isFullyInvoiced) return -1
                return 0
            })

            // Filter by search term if provided
            if (this.scopeSearchFilter) {
                const searchTerm = this.scopeSearchFilter.toLowerCase()
                return scopes.filter(
                    scope =>
                        scope.name.toLowerCase().includes(searchTerm) ||
                        scope.quoteName.toLowerCase().includes(searchTerm)
                )
            }
            return scopes
        },
        isIndeterminate() {
            return (
                this.selectedScopes.length > 0 &&
                this.selectedScopes.length < this.scopes.length
            )
        },
        reactiveInsights() {
            const project = this.project
            const totalInvoiced = this.totalInvoiced
            const forecastedPercentage = this.scopeTotalForecastedPercentage

            return [
                {
                    value: this.formatCurrency(project.currency, project.price),
                    color: '#3498DB',
                    iconColor: '#1A5276',
                    icon: 'mdi-currency-usd',
                    title: 'Project Price',
                },
                {
                    value: totalInvoiced
                        ? this.formatCurrency(project.currency, totalInvoiced)
                        : '-',
                    color: '#27658f',
                    iconColor: '#1A5276',
                    icon: 'mdi-invoice-text-check-outline',
                    title: 'Project Billed',
                },
                {
                    value: this.formatCurrency(
                        project.currency,
                        project.price - totalInvoiced
                    ),
                    color: '#19405c',
                    iconColor: '#1A5276',
                    icon: 'mdi-cash-clock',
                    title: 'Balance To Bill',
                },
                {
                    value: forecastedPercentage + '% ',
                    color:
                        forecastedPercentage == 100
                            ? '#2f9a6b'
                            : forecastedPercentage > 100
                            ? '#cb3c33'
                            : '#d86514',
                    iconColor: '#1A5276',
                    icon: 'mdi-percent-outline',
                    title: 'Forecasted Percentage',
                },
                {
                    value: this.formatCurrency(
                        project.currency,
                        this.initialInvoiced
                    ),
                    color: '#20844c',
                    iconColor: '#2874A6',
                    icon: 'mdi-invoice-outline',
                    title: 'Initial Invoice',
                },
            ]
        },
        // Total scope price
        totalScopePrice() {
            if (this.scopes.length === 0) return 0
            return this.scopes.reduce(
                (acc, scope) => acc + (scope.totalPrice || 0),
                0
            )
        },
        // Total current B2B
        totalCurrentB2B() {
            if (this.scopes.length === 0) return 0
            return (
                this.scopes.reduce(
                    (acc, scope) => acc + (scope.currentBalanceToBill || 0),
                    0
                ) - (this.initialInvoiced || 0)
            )
        },
        // Total base B2B
        totalBaseB2B() {
            if (this.scopes.length === 0) return 0
            return this.scopes.reduce(
                (acc, scope) => acc + (scope.baseBalanceToBill || 0),
                0
            )
        },
        // Total forecasted percentage
        scopeTotalForecastedPercentage() {
            let totalForecasted = 0

            this.scopes.forEach(scope => {
                const dateKeys = Object.keys(scope).filter(key =>
                    /^\d{4}-\d{2}$/.test(key)
                )

                dateKeys.forEach(dateKey => {
                    if (moment(dateKey).isSameOrAfter(this.currentDate)) {
                        totalForecasted += +scope[dateKey].amount || 0
                    }
                })
            })

            if (
                isNaN(totalForecasted) ||
                isNaN(this.totalScopePrice) ||
                totalForecasted === 0 ||
                this.totalScopePrice === 0
            ) {
                return '0.00'
            }

            return ((totalForecasted / this.totalScopePrice) * 100).toFixed(2)
        },
        // Invoiced total percentage
        invoicedTotalPercentage() {
            let totalPrice = 0

            if (this.scopes.length > 0) {
                this.scopes.forEach(scope => {
                    totalPrice += scope.totalPrice || 0
                })
            }

            if (
                isNaN(this.totalInvoiced) ||
                isNaN(totalPrice) ||
                this.totalInvoiced === 0 ||
                totalPrice === 0
            ) {
                return '0.00'
            }

            return ((this.totalInvoiced / totalPrice) * 100).toFixed(2)
        },
        // Total invoiced per date
        totalInvoicedPerDate() {
            let resultPerDate = 0
            this.scopes.forEach(scope => {
                if (this.viewType == 'month') {
                    if (scope.invoicedThisMonth) {
                        resultPerDate += scope.invoicedThisMonth
                    }
                } else if (this.viewType == 'year') {
                    if (scope.invoicedThisYear) {
                        resultPerDate += scope.invoicedThisYear
                    }
                }
            })

            return this.formatCurrency(
                this.project.currency,
                resultPerDate.toFixed(2)
            )
        },
        totalsPerScopeDate() {
            const totals = {}

            // Obtener todas las fechas únicas de los headers
            const allDates = this.scopeHeaders
                .filter(
                    h =>
                        (h.type === 'month' && h.value === 'date') ||
                        (h.type === 'year' && h.value === 'dateYear')
                )
                .map(h => h.month || h.year)
                .filter(Boolean)

            allDates.forEach(date => {
                let resultPerDate = 0
                this.scopes.forEach(scope => {
                    const dateKeys = Object.keys(scope).filter(key =>
                        /^\d{4}-\d{2}$/.test(key)
                    )
                    dateKeys.forEach(dateKey => {
                        if (date.includes('-')) {
                            if (dateKey == date && scope[dateKey]?.amount) {
                                resultPerDate += +scope[dateKey].amount || 0
                            }
                        } else {
                            if (
                                dateKey.includes(date) &&
                                scope[dateKey]?.amount
                            ) {
                                resultPerDate += +scope[dateKey].amount || 0
                            }
                        }
                    })
                })

                const amount = this.formatCurrency(
                    this.project.currency,
                    resultPerDate.toFixed(2)
                )
                const percentage =
                    (resultPerDate * 100) / this.totalScopePrice || 0.0
                totals[date] = percentage.toFixed(2) + '%' + ' - ' + amount
            })

            return totals
        },
    },
    async mounted() {
        try {
            this.loading = true

            // Set current date once
            let currentDate = new Date()
            this.currentDate = `${currentDate.getFullYear()}-${String(
                currentDate.getMonth() + 1
            ).padStart(2, '0')}`

            // Load all data
            const [userData, projectData] = await Promise.all([
                API.getMyInfo(),
                API.getProject(this.projectId),
            ])

            this.user = userData.data.user
            this.project = projectData
            this.forecast = this.project.forecastV2 || { scopes: {} }

            // Load quotes
            await this.getQuotesData()

            // Then load invoices and costs in parallel
            await Promise.all([this.getScopeInvoices(), this.loadCosts()])

            // Create scopes base (static data) once
            this.createScopesBase()

            // Update scopes with forecast data
            this.updateScopesWithForecast()

            // Set headers and insights
            this.setHeaders()
            this.showMonths()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    watch: {
        selectedScopes: {
            handler(newSelectedScopes, oldSelectedScopes) {
                // Don't execute if bulk forecast dialog is open
                if (this.bulkForecastDialog) {
                    return
                }

                // Update selectAll
                this.selectAll =
                    newSelectedScopes.length === this.scopes.length &&
                    this.scopes.length > 0

                // Only execute when scopes are being added (not removed)
                if (
                    newSelectedScopes.length > (oldSelectedScopes?.length || 0)
                ) {
                    // Find the newly selected scope
                    const newlySelected = newSelectedScopes.find(
                        scopeId => !oldSelectedScopes?.includes(scopeId)
                    )
                    if (newlySelected) {
                        const scope = this.scopes.find(
                            s => s.id === newlySelected
                        )
                        if (scope) {
                            this.autoSelectQuoteScopes(scope)
                        }
                    }
                }
            },
            deep: true,
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // Helper: Determine entry type (reference or forecast) based on date
        getEntryType(date) {
            const currentMoment = moment()
            const currentDay = currentMoment.date()
            const currentMonth = currentMoment.month() + 1
            const currentYear = currentMoment.year()

            const targetMoment = moment(date, 'YYYY-MM')
            const targetMonth = targetMoment.month() + 1
            const targetYear = targetMoment.year()

            const isCurrentMonth =
                currentMonth === targetMonth && currentYear === targetYear
            const isFutureMonth =
                targetYear > currentYear ||
                (targetYear === currentYear && targetMonth > currentMonth)

            // Reference: First 5 days of the target month OR any future month
            // Forecast: Day 6 onwards of the current month
            return isFutureMonth || (isCurrentMonth && currentDay <= 5)
                ? 'reference'
                : 'forecast'
        },
        scopeDiffConditions() {
            return Object.keys(this.scopeForecastDiff).length > 0
        },
        autoSelectQuoteScopes(scope) {
            if (!scope || !scope.quoteId) {
                return
            }
            const sameQuoteScopes = this.scopes.filter(
                s => s.quoteId === scope.quoteId
            )
            sameQuoteScopes.forEach(s => {
                if (!this.selectedScopes.includes(s.id)) {
                    this.selectedScopes.push(s.id)
                }
            })
        },
        toggleSelectAll() {
            if (this.selectAll) {
                // Select all scopes
                this.selectedScopes = this.scopes.map(scope => scope.id)
            } else {
                // Deselect all scopes
                this.selectedScopes = []
            }
        },
        openEditScopeMonth(entry, date) {
            const scope = this.scopes.find(s => s.id === this.scopeInEdition)
            if (scope) {
                this.autoSelectQuoteScopes(scope)
            }

            // Get the entry based on current editing mode
            if (this.isEditingInitialProjection) {
                // When editing Initial Projection, prefer lastProjection, then reference
                const scopeForecast = this.forecast.scopes?.[
                    this.scopeInEdition
                ]
                if (
                    scopeForecast &&
                    scopeForecast.dates &&
                    scopeForecast.dates[date]
                ) {
                    const dateEntry = scopeForecast.dates[date]
                    this.originalEntry = dateEntry.lastProjection
                        ? dateEntry.lastProjection
                        : dateEntry.reference
                        ? dateEntry.reference
                        : { date, amount: 0, percentage: 0 }
                } else {
                    this.originalEntry = { date, amount: 0, percentage: 0 }
                }
            } else {
                this.originalEntry = entry
                    ? entry
                    : { date, amount: 0, percentage: 0 }
            }

            this.createDialog = false
            this.newMonthScopeForm = true
        },
        openReplaceScopeForecast(scope) {
            this.autoSelectQuoteScopes(scope)

            this.scopeInEdition = scope.id
            this.selectedScope = scope
            this.isEditingInitialProjection = false
            this.calculateScopeForecastValues(scope)
        },
        openEditInitialProjectionForecast(scope) {
            this.autoSelectQuoteScopes(scope)

            this.scopeInEdition = scope.id
            this.selectedScope = scope
            this.isEditingInitialProjection = true
            this.calculateInitialProjectionValues(scope)
        },
        openBulkForecastDialog() {
            this.bulkForecastDialog = true
        },
        closeBulkForecastDialog() {
            this.bulkForecastDialog = false
        },
        removeScopeFromSelection(scopeId) {
            this.selectedScopes = this.selectedScopes.filter(
                id => id !== scopeId
            )
        },
        async onBulkForecastCreated(updatedForecast) {
            try {
                this.loading = true

                if (updatedForecast) {
                    this.forecast = updatedForecast
                }

                this.calculateScopeUsedDates()
                this.setHeaders()
                this.showMonths()
                this.updateScopesWithForecast()
                this.selectedScopes = []
                this.scopeInEdition = undefined
                this.selectedScope = undefined
                this.closeBulkForecastDialog()

                await this.setUpdate()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        parseCurrency(value) {
            if (value instanceof Promise) {
                value = value.value
            }
            if (value) {
                const parts = value.split(' - ')
                return parts.map(part => part.trim())
            }
            return
        },
        async getQuotesData() {
            try {
                this.quotes = await API.getProjectQuotes(this.project.id)
                this.quotes = this.quotes.map(quote => {
                    return {
                        costExchanges: quote.costExchanges,
                        currency: quote.currency,
                        exchange: quote.exchange,
                        id: quote.id,
                        name: quote.name,
                        number: quote.number,
                        totalCosts: quote.totalCosts,
                    }
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async loadCosts() {
            try {
                if (this.quotes.length > 0) {
                    this.quotes.sort(
                        (a, b) =>
                            (a.createdOn?._seconds ||
                                a.createdOn?.seconds ||
                                0) -
                            (b.createdOn?._seconds || b.createdOn?.seconds || 0)
                    )

                    const startDateSeconds =
                        this.quotes[0]?.createdOn?._seconds ||
                        this.quotes[0]?.createdOn?.seconds

                    const startDate = startDateSeconds
                        ? moment.unix(startDateSeconds).format('YYYY-MM-DD')
                        : moment()
                              .subtract(1, 'year')
                              .format('YYYY-MM-DD')

                    const endDateSeconds =
                        this.quotes[this.quotes.length - 1]?.updatedOn
                            ?._seconds ||
                        this.quotes[this.quotes.length - 1]?.updatedOn?.seconds

                    const endDate = endDateSeconds
                        ? moment.unix(endDateSeconds).format('YYYY-MM-DD')
                        : moment().format('YYYY-MM-DD')

                    // get quotes ids
                    let ids = this.quotes
                        .map(quote => quote?.id)
                        .filter(id => id)

                    if (ids.length > 0) {
                        const costsData = await API.getForecastCosts(
                            startDate,
                            endDate,
                            ids
                        )
                        this.costs = costsData || {}
                    } else {
                        this.costs = {}
                    }
                } else {
                    this.costs = {}
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        createScopesBase() {
            let scopes = []
            const seenScopeIds = new Set()

            if (!this.costs || typeof this.costs !== 'object') {
                this.costs = {}
            }

            if (this.quotes.length > 0) {
                for (let quote of this.quotes) {
                    if (quote.id) {
                        // Filter costs for this quote
                        const costsArray = Object.values(this.costs || {})
                        const quoteCosts = costsArray.filter(
                            cost =>
                                cost &&
                                cost.quoteId === quote.id &&
                                cost.type === 'cost'
                        )

                        quoteCosts.forEach(scope => {
                            if (!scope?.id || seenScopeIds.has(scope.id)) {
                                return
                            }
                            seenScopeIds.add(scope.id)

                            // Get invoice data for this scope
                            const invoiceData =
                                this.invoicedPerScope[scope.id] || {}

                            // Create scope object with direct structure
                            const newScope = {
                                id: scope.id,
                                index: Number(scope.index),
                                name: scope.reference,
                                quoteName: quote.name,
                                quoteId: quote.id,
                                quote: quote,
                                quoteCurrency: quote.currency,
                                totalPrice: Number(scope.salePrice2 || 0),
                                pastMonthInvoiced: Number(
                                    invoiceData.pastMonths || 0
                                ),
                                invoicedThisMonth: Number(
                                    invoiceData.thisMonth || 0
                                ),
                                totalInvoiced: Number(invoiceData.total || 0),
                                invoicedThisYear: Number(
                                    invoiceData.thisYear || 0
                                ),
                                currentBalanceToBill:
                                    Number(scope.salePrice2 || 0) -
                                    Number(invoiceData.total || 0),
                                baseBalanceToBill:
                                    Number(scope.salePrice2 || 0) -
                                    Number(invoiceData.pastMonths || 0),
                                actualVsForecasted: 0, // Default value
                            }

                            scopes.push(newScope)
                        })
                    }
                }
            }
            if (this.invoicedPerScope['free'] && !seenScopeIds.has('free')) {
                seenScopeIds.add('free')
                scopes.push({
                    id: 'free',
                    index: Number(0),
                    name: 'Initial billing',
                    quoteName: 'Initial billing',
                    quoteId: 'free',
                    quote: {},
                    quoteCurrency: 'usd',
                    totalPrice: 0,
                    pastMonthInvoiced: Number(
                        this.invoicedPerScope['free']?.pastMonths || 0
                    ),
                    invoicedThisMonth: Number(
                        this.invoicedPerScope['free']?.thisMonth || 0
                    ),
                    totalInvoiced: Number(
                        this.invoicedPerScope['free']?.total || 0
                    ),
                    invoicedThisYear: Number(
                        this.invoicedPerScope['free']?.thisYear || 0
                    ),
                    currentBalanceToBill: 0,
                    baseBalanceToBill: 0,
                    actualVsForecasted: 0, // Default value
                })
            }
            this.initialInvoiced = this.invoicedPerScope['free']?.total || 0

            this.scopes = scopes
        },
        // Update scopes with forecast data
        updateScopesWithForecast() {
            this.scopes.forEach(scope => {
                // Get forecast data for this scope
                const forecastData = this.forecast.scopes?.[scope.id]

                if (forecastData?.dates) {
                    // Update forecast data using helper
                    this.addForecastDataToScope(scope, forecastData)
                } else {
                    // Ensure actualVsForecasted has a default value even without forecast data
                    scope.actualVsForecasted = 0
                }
            })
        },
        // Helper method to add forecast data to scope
        // Uses display context hierarchy: reference > lastProjection > forecast
        addForecastDataToScope(scope, forecastData) {
            const currentDate = moment().format('YYYY-MM')
            let totalForecasted = 0

            Object.entries(forecastData.dates || {}).forEach(([dateKey]) => {
                // Use helper method to get entry with hierarchy
                const entry = this.getProjectionEntry(forecastData.id, dateKey)
                if (entry && entry.date) {
                    this.$set(scope, dateKey, entry)

                    if (moment(dateKey).isSameOrAfter(currentDate)) {
                        totalForecasted += parseFloat(entry.amount || 0)
                    }
                }
            })

            scope.actualVsForecasted =
                totalForecasted === 0
                    ? 0
                    : (parseFloat(scope.invoicedThisMonth) * 100) /
                      Math.abs(totalForecasted)
        },
        async getScopeInvoices() {
            let invoices = await API.getProjectInvoices(this.project.id)

            // get initial invoice
            // invoices.forEach(invoice => {
            //     if (invoice.items['free']) {
            //         invoice.items['free'].forEach(item => {
            //             this.initialInvoiced += +(item.currentInvoice || 0)
            //         })
            //     }
            // })

            // filter invoices
            invoices = invoices.filter(
                invoice =>
                    invoice.status != 'rejected' &&
                    invoice.status != 'inProgress' &&
                    invoice.status != 'generating'
            )
            invoices.forEach(invoice => {
                const seconds =
                    invoice.billedOn?._seconds ?? invoice.createdOn?._seconds
                if (seconds) {
                    invoice.date = this.formatDate(seconds)
                } else {
                    invoice.date = null
                }
            })
            this.invoicedPerScope = {}

            // get total invoiced per scope
            invoices.forEach(invoice => {
                if (invoice.items) {
                    for (let key in invoice.items) {
                        for (let cost of invoice.items[key]) {
                            let scopeId = 'free'
                            if (cost.costId) {
                                scopeId = cost.costId
                            }

                            if (!this.invoicedPerScope[scopeId]) {
                                this.invoicedPerScope[scopeId] = {
                                    id: scopeId,
                                    total: 0,
                                    thisMonth: 0,
                                    thisYear: 0,
                                    pastMonths: 0,
                                }
                            }
                            let invoiced = 0
                            if (
                                cost.pricePerUnit &&
                                !isNaN(Number(cost.pricePerUnit))
                            ) {
                                let billedQty = Number(
                                    cost.billedQty ?? cost.invoiceQuantity ?? 0
                                )
                                if (
                                    billedQty === 0 &&
                                    (cost.creditNoteQty || cost.creditQty) &&
                                    Number(
                                        cost.creditNoteQty ??
                                            cost.creditQty ??
                                            0
                                    ) > 0
                                ) {
                                    billedQty = Number(
                                        cost.creditNoteQty ?? cost.creditQty
                                    )
                                }
                                const creditQty = invoice.totalCredit
                                    ? 0
                                    : Number(
                                          cost.creditQty ??
                                              cost.creditQtyRequested ??
                                              cost.creditNoteQty ??
                                              0
                                      )

                                invoiced =
                                    Number(cost.pricePerUnit || 0) *
                                    (billedQty - creditQty)
                            }
                            this.invoicedPerScope[scopeId].total += invoiced
                        }
                    }
                }

                // get invoiced in past months per scope
                if (invoice.date) {
                    const invoiceMonth = moment
                        .unix(
                            invoice.billedOn?._seconds ??
                                invoice.createdOn?._seconds
                        )
                        .format('YYYY-MM')
                    const pastMonth = moment(this.currentDate, 'YYYY-MM-DD')
                        .subtract(1, 'month')
                        .format('YYYY-MM')
                    if (invoiceMonth <= pastMonth) {
                        if (invoice.items) {
                            Object.keys(invoice.items).forEach(quoteId => {
                                invoice.items[quoteId].forEach(item => {
                                    const scopeId = item.costId
                                    let invoicedInPastMonths = 0
                                    if (
                                        item.pricePerUnit &&
                                        !isNaN(Number(item.pricePerUnit))
                                    ) {
                                        let billedQty = Number(
                                            item.billedQty ??
                                                item.invoiceQuantity ??
                                                0
                                        )
                                        if (
                                            billedQty === 0 &&
                                            (item.creditNoteQty ||
                                                item.creditQty) &&
                                            Number(
                                                item.creditNoteQty ??
                                                    item.creditQty ??
                                                    0
                                            ) > 0
                                        ) {
                                            billedQty = Number(
                                                item.creditNoteQty ??
                                                    item.creditQty
                                            )
                                        }
                                        const creditQty = invoice.totalCredit
                                            ? 0
                                            : Number(
                                                  item.creditQty ??
                                                      item.creditQtyRequested ??
                                                      item.creditNoteQty ??
                                                      0
                                              )

                                        invoicedInPastMonths =
                                            Number(item.pricePerUnit || 0) *
                                            (billedQty - creditQty)
                                    }

                                    if (this.invoicedPerScope[scopeId]) {
                                        this.invoicedPerScope[
                                            scopeId
                                        ].pastMonths += +(
                                            invoicedInPastMonths || 0
                                        )
                                    } else {
                                        this.invoicedPerScope[scopeId] = {
                                            pastMonths: +(
                                                invoicedInPastMonths || 0
                                            ),
                                        }
                                    }
                                })
                            })
                        }
                    }
                }

                // get invoiced this month per scope
                if (invoice.date) {
                    const invoiceMonth = moment
                        .unix(
                            invoice.date?._seconds ??
                                invoice.createdOn?._seconds
                        )
                        .format('YYYY-MM')
                    if (invoiceMonth == this.currentDate) {
                        if (invoice.items) {
                            Object.keys(invoice.items).forEach(quoteId => {
                                invoice.items[quoteId].forEach(item => {
                                    const scopeId = item.costId

                                    let invoicedInMonth = 0
                                    if (
                                        item.pricePerUnit &&
                                        !isNaN(Number(item.pricePerUnit))
                                    ) {
                                        let billedQty = Number(
                                            item.billedQty ??
                                                item.invoiceQuantity ??
                                                0
                                        )
                                        if (
                                            billedQty === 0 &&
                                            (item.creditNoteQty ||
                                                item.creditQty) &&
                                            Number(
                                                item.creditNoteQty ??
                                                    item.creditQty ??
                                                    0
                                            ) > 0
                                        ) {
                                            billedQty = Number(
                                                item.creditNoteQty ??
                                                    item.creditQty
                                            )
                                        }
                                        const creditQty = invoice.totalCredit
                                            ? 0
                                            : Number(
                                                  item.creditQty ??
                                                      item.creditQtyRequested ??
                                                      item.creditNoteQty ??
                                                      0
                                              )

                                        invoicedInMonth =
                                            Number(item.pricePerUnit || 0) *
                                            (billedQty - creditQty)
                                    }

                                    if (this.invoicedPerScope[scopeId]) {
                                        this.invoicedPerScope[
                                            scopeId
                                        ].thisMonth += +(invoicedInMonth || 0)
                                    } else {
                                        this.invoicedPerScope[scopeId] = {
                                            thisMonth: +(invoicedInMonth || 0),
                                        }
                                    }
                                })
                            })
                        }
                    }
                }

                // get invoiced this year per scope
                if (invoice.date) {
                    const invoiceYear = moment
                        .unix(
                            invoice.date?._seconds ??
                                invoice.createdOn?._seconds
                        )
                        .format('YYYY')
                    const currentYear = _.cloneDeep(
                        this.currentDate.split('-')[0]
                    )
                    if (invoiceYear == currentYear) {
                        if (invoice.items) {
                            Object.keys(invoice.items).forEach(quoteId => {
                                invoice.items[quoteId].forEach(item => {
                                    const scopeId = item.costId
                                    let invoicedInMonth = 0
                                    if (
                                        item.pricePerUnit &&
                                        !isNaN(Number(item.pricePerUnit))
                                    ) {
                                        let billedQty = Number(
                                            item.billedQty ??
                                                item.invoiceQuantity ??
                                                0
                                        )
                                        if (
                                            billedQty === 0 &&
                                            (item.creditNoteQty ||
                                                item.creditQty) &&
                                            Number(
                                                item.creditNoteQty ??
                                                    item.creditQty ??
                                                    0
                                            ) > 0
                                        ) {
                                            billedQty = Number(
                                                item.creditNoteQty ??
                                                    item.creditQty
                                            )
                                        }
                                        const creditQty = invoice.totalCredit
                                            ? 0
                                            : Number(
                                                  item.creditQty ??
                                                      item.creditQtyRequested ??
                                                      item.creditNoteQty ??
                                                      0
                                              )

                                        invoicedInMonth =
                                            Number(item.pricePerUnit || 0) *
                                            (billedQty - creditQty)
                                    }

                                    if (this.invoicedPerScope[scopeId]) {
                                        this.invoicedPerScope[
                                            scopeId
                                        ].thisYear += +(invoicedInMonth || 0)
                                    } else {
                                        this.invoicedPerScope[scopeId] = {
                                            thisYear: +(invoicedInMonth || 0),
                                        }
                                    }
                                })
                            })
                        }
                    }
                }
                if (invoice.totalCredit && invoice.items) {
                    const valuesPerScope = {}
                    let grandTotal = 0
                    Object.values(invoice.items).forEach(costList => {
                        costList.forEach(cost => {
                            const scopeId = cost.costId || 'free'
                            if (
                                !cost.pricePerUnit ||
                                isNaN(Number(cost.pricePerUnit))
                            ) {
                                return
                            }
                            let billedQty = Number(
                                cost.billedQty ?? cost.invoiceQuantity ?? 0
                            )
                            if (
                                billedQty === 0 &&
                                (cost.creditNoteQty || cost.creditQty) &&
                                Number(
                                    cost.creditNoteQty ?? cost.creditQty ?? 0
                                ) > 0
                            ) {
                                billedQty = Number(
                                    cost.creditNoteQty ?? cost.creditQty
                                )
                            }
                            const value = Number(cost.pricePerUnit) * billedQty
                            valuesPerScope[scopeId] =
                                (valuesPerScope[scopeId] || 0) + value
                            grandTotal += value
                        })
                    })

                    if (grandTotal > 0) {
                        const totalCredit = Number(invoice.totalCredit)
                        const invoiceSeconds =
                            invoice.billedOn?._seconds ??
                            invoice.createdOn?._seconds
                        const invoiceMonth = invoiceSeconds
                            ? moment.unix(invoiceSeconds).format('YYYY-MM')
                            : null
                        const invoiceYear = invoiceMonth
                            ? invoiceMonth.split('-')[0]
                            : null
                        const pastMonthLimit = moment(
                            this.currentDate,
                            'YYYY-MM-DD'
                        )
                            .subtract(1, 'month')
                            .format('YYYY-MM')
                        const currentYear = this.currentDate.split('-')[0]

                        Object.keys(valuesPerScope).forEach(scopeId => {
                            const portion =
                                (valuesPerScope[scopeId] / grandTotal) *
                                totalCredit
                            if (!this.invoicedPerScope[scopeId]) {
                                this.invoicedPerScope[scopeId] = {
                                    id: scopeId,
                                    total: 0,
                                    thisMonth: 0,
                                    thisYear: 0,
                                    pastMonths: 0,
                                }
                            }
                            this.invoicedPerScope[scopeId].total =
                                (this.invoicedPerScope[scopeId].total || 0) -
                                portion
                            if (
                                invoiceMonth &&
                                invoiceMonth <= pastMonthLimit
                            ) {
                                this.invoicedPerScope[scopeId].pastMonths =
                                    (this.invoicedPerScope[scopeId]
                                        .pastMonths || 0) - portion
                            }
                            if (invoiceMonth === this.currentDate) {
                                this.invoicedPerScope[scopeId].thisMonth =
                                    (this.invoicedPerScope[scopeId].thisMonth ||
                                        0) - portion
                            }
                            if (invoiceYear === currentYear) {
                                this.invoicedPerScope[scopeId].thisYear =
                                    (this.invoicedPerScope[scopeId].thisYear ||
                                        0) - portion
                            }
                        })
                    }
                }

                this.totalcredits += Number(invoice.totalCredit || 0)
            })
            this.invoices = invoices
        },
        getEntryByContext(dateEntry, context = 'display') {
            if (!dateEntry || typeof dateEntry !== 'object') return null

            if (context === 'editingInitial') {
                if (dateEntry.lastProjection) return dateEntry.lastProjection
                if (dateEntry.reference) return dateEntry.reference
                if (dateEntry.forecast) return dateEntry.forecast
                return null
            }

            if (context === 'editingRegular' || context === 'display') {
                if (dateEntry.forecast) return dateEntry.forecast
                if (dateEntry.lastProjection) return dateEntry.lastProjection
                if (dateEntry.reference) return dateEntry.reference
                return null
            }

            return null
        },
        getCurrentContext() {
            if (this.isEditingInitialProjection && this.scopeInEdition) {
                return 'editingInitial'
            }
            if (this.scopeInEdition) {
                return 'editingRegular'
            }
            return 'display'
        },

        getProjectionEntry(scopeId, date, forcedContext = null) {
            const dateEntry = this.forecast.scopes?.[scopeId]?.dates?.[date]
            if (!dateEntry) return null

            const context = forcedContext || this.getCurrentContext()
            return this.getEntryByContext(dateEntry, context)
        },

        createProjectionEntry(scopeId, date, amount, percentage) {
            if (!this.forecast) {
                this.forecast = { scopes: {} }
            }
            if (!this.forecast.scopes) {
                this.forecast.scopes = {}
            }

            if (!this.forecast.scopes[scopeId]) {
                this.$set(this.forecast.scopes, scopeId, {
                    id: scopeId,
                    dates: {},
                })
            }

            if (!this.forecast.scopes[scopeId].dates) {
                this.$set(this.forecast.scopes[scopeId], 'dates', {})
            }

            const dateKey = date
            // If editing initial projection, use lastProjection, otherwise use getEntryType
            const entryType = this.isEditingInitialProjection
                ? 'lastProjection'
                : this.getEntryType(date)
            const entryData = {
                date,
                amount: parseFloat(amount || 0),
                percentage: parseFloat(percentage || 0),
            }

            const currentDateEntry =
                this.forecast.scopes[scopeId].dates[dateKey] || {}

            // Preserve existing properties when updating
            this.$set(this.forecast.scopes[scopeId].dates, dateKey, {
                ...currentDateEntry,
                [entryType]: entryData,
            })

            // Update scope in scopes array for reactivity
            this.updateScopeInArray(scopeId, date)
        },
        // Helper: Update scope in scopes array for reactivity
        updateScopeInArray(scopeId, date) {
            const scope = this.scopes.find(s => s.id === scopeId)
            if (!scope) return

            const entry = this.getProjectionEntry(scopeId, date)

            if (entry) {
                this.$set(scope, date, entry)
            } else {
                this.$set(scope, date, { date, amount: 0, percentage: 0 })
            }
        },
        // Helper: Refresh forecast view after changes
        async refreshForecastView() {
            this.setHeaders()
            this.showMonths()
            this.updateScopesWithForecast()
            this.calculateScopeUsedDates()
        },
        getDateScopePercentage(item, date) {
            if (date.includes('-')) {
                if (item[date] && item[date].amount && item.totalPrice) {
                    const amount = this.getScopeDateAmount(item, date)
                    const percentage = (
                        (+amount * 100) /
                        item.totalPrice
                    ).toFixed(2)
                    return percentage
                }
            } else {
                const amount = this.getScopeDateAmount(item, date)
                const percentage = ((+amount * 100) / item.totalPrice).toFixed(
                    2
                )
                return percentage
            }
            return '0.00'
        },
        /**
         * Get the amount for a scope and date
         * Automatically uses the correct hierarchy based on context
         */
        getScopeDateAmount(item, date) {
            if (date.includes('-')) {
                // For specific month: check item first (from scopes array - already reactive)
                if (item[date] && item[date].amount !== undefined) {
                    return parseFloat(item[date].amount) || 0
                }

                // Fallback: read directly from forecast using helper with current context
                const entry = this.getProjectionEntry(item.id, date)
                return entry?.amount ? parseFloat(entry.amount) : 0
            } else {
                // For year: sum all months
                let amount = 0
                const forecastScope = this.forecast.scopes?.[item.id]

                if (forecastScope?.dates) {
                    Object.entries(forecastScope.dates).forEach(
                        ([forecastDate]) => {
                            if (forecastDate.split('-')[0] === date) {
                                // Check item[forecastDate] first for reactivity
                                if (
                                    item[forecastDate] &&
                                    item[forecastDate].amount !== undefined
                                ) {
                                    amount += parseFloat(
                                        item[forecastDate].amount
                                    )
                                } else {
                                    // Fallback: use helper with current context
                                    const entry = this.getProjectionEntry(
                                        item.id,
                                        forecastDate
                                    )
                                    if (entry?.amount) {
                                        amount += parseFloat(entry.amount)
                                    }
                                }
                            }
                        }
                    )
                }
                return amount
            }
        },

        getMonthReferenceInfo(item, date) {
            if (!this.forecast || !this.forecast.scopes) return null
            const dateEntry = this.forecast.scopes[item.id]?.dates?.[date]
            if (!dateEntry || typeof dateEntry !== 'object') return null

            // Priority: lastProjection > reference
            if (
                dateEntry.lastProjection &&
                typeof dateEntry.lastProjection === 'object'
            ) {
                return dateEntry.lastProjection
            }
            if (
                dateEntry.reference &&
                typeof dateEntry.reference === 'object'
            ) {
                return dateEntry.reference
            }

            return null
        },

        getMonthForecastInfo(item, date) {
            if (!this.forecast || !this.forecast.scopes) return null
            const dateEntry = this.forecast.scopes[item.id]?.dates?.[date]
            if (!dateEntry || typeof dateEntry !== 'object') return null
            if (!dateEntry.forecast || typeof dateEntry.forecast !== 'object')
                return null
            return dateEntry.forecast
        },
        formatCurrency(currency, value) {
            if (currency && value) {
                const truncated = Math.trunc(+value)
                const formattedValue = new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: currency,
                    maximumFractionDigits: 0,
                }).format(truncated)

                if (Math.abs((+value || 0).toFixed(0)) == 0) {
                    return '-'
                }
                return formattedValue
            } else if (currency && !value) {
                return '-'
            }
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('YYYY-MM')}`
        },
        calculateScopeUsedDates() {
            this.allDates = [this.currentDate]
            if (this.forecast && this.forecast.scopes) {
                Object.values(this.forecast.scopes).forEach(scope => {
                    if (scope && scope.dates) {
                        Object.values(scope.dates).forEach(entry => {
                            if (entry?.reference?.date) {
                                this.allDates.push(entry.reference.date)
                            }
                            // Include lastProjection dates (initial projection with permission)
                            // Both reference and lastProjection are initial projections
                            if (entry?.lastProjection?.date) {
                                this.allDates.push(entry.lastProjection.date)
                            }
                            // Include forecast dates (current projection)
                            if (entry?.forecast?.date) {
                                this.allDates.push(entry.forecast.date)
                            }
                            // include entry.date if no reference/forecast/lastProjection
                            if (
                                entry.date &&
                                !entry.reference &&
                                !entry.forecast &&
                                !entry.lastProjection
                            ) {
                                this.allDates.push(entry.date)
                            }
                        })
                    }
                })
                this.dateToShow = [...new Set(this.allDates)].sort((a, b) =>
                    moment(a).diff(moment(b))
                )
                const minDate = this.currentDate
                const maxDate = this.dateToShow[this.dateToShow.length - 1]
                this.dateToShow = this.monthsBetweenDates(
                    moment(minDate),
                    moment(maxDate)
                )
            }
        },
        setHeaders() {
            const currentMonth = moment()
                .format('MMM')
                .toUpperCase()
            const currentYear = moment().year()
            const invoicedHeaderText = `INVOICED IN ${currentMonth}`
            const invoicedHeaderTextYear = `INVOICED IN ${currentYear}`
            this.originalHeaders = [
                {
                    text: '',
                    value: 'select',
                    align: 'center',
                    sortable: false,
                    class: 'gray-background',
                    width: 5,
                },
                {
                    text: 'SCOPE',
                    value: 'name',
                    align: 'end',
                    sortable: true,
                    class: 'gray-background',
                    width: 270,
                },
                {
                    text: 'QUOTE',
                    value: 'quoteName',
                    align: 'end',
                    sortable: true,
                    class: 'gray-background',
                    width: 250,
                },
                {
                    text: 'PRICE',
                    value: 'totalPrice',
                    align: 'end',
                    sortable: false,
                    width: 150,
                    class: 'gray-background',
                },
                {
                    text: 'CURRENT BALANCE TO BILL',
                    value: 'currentBalanceToBill',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 140,
                },
                {
                    text: 'BASE BALANCE TO BILL',
                    value: 'baseBalanceToBill',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                },
                {
                    text: 'ACTUAL VS FORECASTED',
                    value: 'actualVsForecasted',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                },
                {
                    text: invoicedHeaderText,
                    value: 'invoicedThisMonth',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                    type: 'month',
                },
                {
                    text: invoicedHeaderTextYear,
                    value: 'invoicedThisYear',
                    align: 'end',
                    sortable: false,
                    width: 150,
                    class: 'gray-background',
                    type: 'year',
                },
                {
                    text: 'TOTAL INVOICED',
                    value: 'totalInvoiced',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                },
                {
                    text: 'INVOICED PERCENTAGE',
                    value: 'invoicedPercentage',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                },
                {
                    text: 'FORECASTED PERCENTAGE',
                    value: 'forecastedPercentage',
                    align: 'end',
                    sortable: false,
                    class: 'gray-background',
                    width: 150,
                },
            ]

            this.calculateScopeUsedDates()

            let yearClasses = {}
            let toggleClass = true

            this.dateToShow.forEach(date => {
                const dateParts = date.split('-')
                const monthIndex = +dateParts[1] - 1
                const year = dateParts[0]
                const dateText = `${this.months[monthIndex]}-${year}`

                if (!yearClasses[year]) {
                    yearClasses[year] = toggleClass
                        ? 'dark-gray-background'
                        : 'gray-background'
                    toggleClass = !toggleClass
                }
                this.originalHeaders.push({
                    text: dateText,
                    value: 'date',
                    align: 'end',
                    sortable: false,
                    width: 150,
                    type: 'month',
                    month: date,
                    class: yearClasses[year],
                })
            })

            this.dateToShow.forEach(date => {
                const dateParts = date.split('-')
                const year = dateParts[0]
                const existent = this.originalHeaders.find(h => h.text == year)
                if (!existent) {
                    this.originalHeaders.push({
                        text: year,
                        value: 'dateYear',
                        align: 'end',
                        sortable: false,
                        width: 150,
                        type: 'year',
                        class: yearClasses[year],
                        year,
                    })
                }
            })
            this.originalHeaders.push({
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
                class: 'fixed-column gray-background',
            })
        },
        calculateScopeForecastValues(scope) {
            if (!this.forecast) {
                this.forecast = { scopes: {} }
            }
            if (!this.forecast.scopes) {
                this.forecast.scopes = {}
            }

            const forecastKey = Object.keys(this.forecast.scopes).find(
                key => this.forecast.scopes[key].id == this.scopeInEdition
            )

            this.calculateScopeUsedDates()
            this.originalForecast2 = _.cloneDeep(this.forecast)
            this.originalScopes = _.cloneDeep(this.scopes)
            this.originalDatesToShow = _.cloneDeep(this.dateToShow)

            // set the value of baseBalanceToBill
            // scope.baseBalanceToBill = scope.currentBalanceToBill

            if (forecastKey) {
                this.forecast.scopes[forecastKey].balanceToBill =
                    scope.currentBalanceToBill || 0

                if (this.forecast.scopes[forecastKey].dates) {
                    Object.values(
                        this.forecast.scopes[forecastKey].dates
                    ).forEach(entry => {
                        if (entry.forecast) {
                            entry.forecast.percentage =
                                (
                                    ((+entry.forecast.amount || 0) * 100) /
                                    scope.totalPrice
                                ).toFixed(2) || 0.0
                        }
                    })
                }
            } else {
                this.forecast.scopes[scope.id] = {
                    id: scope.id,
                    balanceToBill: scope.totalPrice,
                    dates: {},
                }
            }
            this.selectedScopeForecast = this.forecast.scopes[scope.id]
        },
        calculateInitialProjectionValues(scope) {
            if (!this.forecast) {
                this.forecast = { scopes: {} }
            }
            if (!this.forecast.scopes) {
                this.forecast.scopes = {}
            }

            const forecastKey = Object.keys(this.forecast.scopes).find(
                key => this.forecast.scopes[key].id == this.scopeInEdition
            )

            this.calculateScopeUsedDates()
            this.originalForecast2 = _.cloneDeep(this.forecast)
            this.originalScopes = _.cloneDeep(this.scopes)
            this.originalDatesToShow = _.cloneDeep(this.dateToShow)

            if (forecastKey) {
                this.forecast.scopes[forecastKey].balanceToBill =
                    scope.currentBalanceToBill || 0

                // Recalculate percentages of lastProjection if they exist
                if (this.forecast.scopes[forecastKey].dates) {
                    Object.values(
                        this.forecast.scopes[forecastKey].dates
                    ).forEach(entry => {
                        if (entry.lastProjection) {
                            entry.lastProjection.percentage =
                                (
                                    ((+entry.lastProjection.amount || 0) *
                                        100) /
                                    scope.totalPrice
                                ).toFixed(2) || 0.0
                        }
                    })
                }
            } else {
                this.forecast.scopes[scope.id] = {
                    id: scope.id,
                    balanceToBill: scope.totalPrice,
                    dates: {},
                }
            }
            this.selectedScopeForecast = this.forecast.scopes[scope.id]
        },
        closeScopeReplaceForecast() {
            this.deletionAction = false
            this.scopeInEdition = undefined
            this.selectedScope = undefined
            this.selectedScopes = []
            this.isEditingInitialProjection = false
            this.scopes = _.cloneDeep(this.originalScopes)
            this.dateToShow = _.cloneDeep(this.originalDatesToShow)
            this.forecast = _.cloneDeep(this.originalForecast2)
            this.setHeaders()
            this.getInsights()
        },
        async replaceScopeForecast() {
            try {
                this.loading = true
                this.selectedScopeForecast = this.forecast.scopes[
                    this.scopeInEdition
                ]

                let entries = []
                let response

                if (this.isEditingInitialProjection) {
                    // Convert only lastProjection entries
                    entries = this.convertInitialProjectionToBulkEntries()

                    response = await API.updateInitialProjection({
                        projectId: this.project.id,
                        entries: entries,
                    })
                } else {
                    // Convert forecast and reference entries
                    entries = this.convertForecastToBulkEntries()

                    response = await API.updateBulkForecast({
                        projectId: this.project.id,
                        entries: entries,
                    })
                }

                this.scopeInEdition = undefined
                this.isEditingInitialProjection = false
                this.forecast = response
                this.updateScopesWithForecast()
                this.calculateScopeUsedDates()
                await this.setUpdate()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.deletionAction = false
                this.loading = false
            }
        },
        async setUpdate() {
            this.$emit('setUpdate', this.forecast, this.project.id)
        },
        showMonths() {
            this.scopeHeaders = this.originalHeaders.filter(
                h => h.type != 'year'
            )
            this.viewType = 'month'
        },
        showYears() {
            this.scopeHeaders = this.originalHeaders.filter(
                h => h.type != 'month'
            )
            this.viewType = 'year'
        },
        monthsBetweenDates(startDate, endDate) {
            let datesObject = []
            let currentDay = startDate
            let dates = []
            while (currentDay.isSameOrBefore(endDate)) {
                dates.push(currentDay.format('YYYY-MM'))
                datesObject[currentDay.format('YYYY-MM')] = 0
                currentDay.add(1, 'month')
            }
            return dates
        },
        // forecasted percentage per scope
        calculateScopeForecastedPercentage(scope) {
            let totalForecasted = 0

            const dateKeys = Object.keys(scope).filter(key =>
                /^\d{4}-\d{2}$/.test(key)
            )

            dateKeys.forEach(dateKey => {
                if (moment(dateKey).isSameOrAfter(this.currentDate)) {
                    totalForecasted += +scope[dateKey].amount || 0
                }
            })

            // if values are not valid
            if (
                (isNaN(totalForecasted) ||
                    isNaN(scope.totalPrice) ||
                    totalForecasted === 0,
                scope.totalPrice === 0)
            ) {
                return '0.00'
            }

            return ((totalForecasted / scope.totalPrice) * 100).toFixed(2)
        },
        // invoiced percentage per scope
        calculateScopeInvoicedPercentage(scope) {
            const totalInvoiced = parseFloat(scope.totalInvoiced)
            const totalPrice = parseFloat(scope.totalPrice)

            // if values are not valid
            if (
                isNaN(totalInvoiced) ||
                isNaN(totalPrice) ||
                totalInvoiced === 0 ||
                totalPrice === 0
            ) {
                return '0.00'
            }

            return ((totalInvoiced / totalPrice) * 100).toFixed(2)
        },
        openAddNewScopeMonth() {
            this.createDialog = true
            this.originalEntry = {}
            this.newMonthScopeForm = true
        },
        openDeleteScopeMonths(scope) {
            this.deleteScopeMonthsDialog = true
            this.originalEntry = _.cloneDeep(scope)
        },
        async deleteScopeMonths() {
            try {
                this.loading = true
                this.selectedScopeForecast = _.cloneDeep(
                    this.forecast.scopes[this.scopeInEdition]
                )
                this.forecast.scopes = this.forecast.scopes || {}
                this.forecast.scopes[this.scopeInEdition] = this.forecast
                    .scopes[this.scopeInEdition] || { dates: {} }

                const scope = this.forecast.scopes[this.scopeInEdition]

                // get date keys of scope
                const dateKeys = Object.keys(scope.dates).filter(key =>
                    /^\d{4}-\d{2}$/.test(key)
                )

                // for date key, only change the forecast.
                dateKeys.forEach(dateKey => {
                    if (
                        moment(dateKey).isSameOrAfter(this.currentDate) &&
                        scope.dates[dateKey].forecast
                    ) {
                        if (scope.dates[dateKey].forecast) {
                            scope.dates[dateKey].forecast.percentage = 0
                            scope.dates[dateKey].forecast.amount = 0
                        }
                    }
                })

                this.deletionAction = true

                this.setHeaders()
                this.showMonths()
                this.updateScopesWithForecast()
                this.calculateScopeUsedDates()

                this.closeDeleteScopesMonthsDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteScopesMonthsDialog() {
            this.deleteScopeMonthsDialog = false
        },
        closeDialog() {
            this.newMonthScopeForm = false
        },
        async addEntry(newEntry) {
            try {
                // If we're in editing mode (scopeInEdition is set), don't save immediately
                if (this.scopeInEdition) {
                    // If editing initial projection, create lastProjection locally
                    if (this.isEditingInitialProjection) {
                        if (!this.forecast.scopes[this.scopeInEdition]) {
                            this.$set(
                                this.forecast.scopes,
                                this.scopeInEdition,
                                {
                                    id: this.scopeInEdition,
                                    dates: {},
                                }
                            )
                        }
                        if (!this.forecast.scopes[this.scopeInEdition].dates) {
                            this.$set(
                                this.forecast.scopes[this.scopeInEdition],
                                'dates',
                                {}
                            )
                        }

                        const entryData = {
                            date: newEntry.date,
                            amount: parseFloat(newEntry.amount || 0),
                            percentage: parseFloat(newEntry.percentage || 0),
                        }

                        const currentDateEntry =
                            this.forecast.scopes[this.scopeInEdition].dates[
                                newEntry.date
                            ] || {}

                        this.$set(
                            this.forecast.scopes[this.scopeInEdition].dates,
                            newEntry.date,
                            {
                                ...currentDateEntry,
                                lastProjection: entryData,
                            }
                        )

                        this.updateScopeInArray(
                            this.scopeInEdition,
                            newEntry.date
                        )
                        await this.refreshForecastView()
                        return
                    }

                    // Normal editing mode: create forecast/reference locally
                    this.createProjectionEntry(
                        this.scopeInEdition,
                        newEntry.date,
                        newEntry.amount,
                        newEntry.percentage
                    )
                    await this.refreshForecastView()
                    return
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async replaceEntry(newEntry) {
            try {
                if (this.isEditingInitialProjection) {
                    // Initialize structure if needed
                    if (!this.forecast.scopes[this.scopeInEdition]) {
                        this.$set(this.forecast.scopes, this.scopeInEdition, {
                            id: this.scopeInEdition,
                            dates: {},
                        })
                    }
                    if (!this.forecast.scopes[this.scopeInEdition].dates) {
                        this.$set(
                            this.forecast.scopes[this.scopeInEdition],
                            'dates',
                            {}
                        )
                    }

                    const dateKey = newEntry.date
                    const entryData = {
                        date: newEntry.date,
                        amount: parseFloat(newEntry.amount || 0),
                        percentage: parseFloat(newEntry.percentage || 0),
                    }

                    const currentDateEntry =
                        this.forecast.scopes[this.scopeInEdition].dates[
                            dateKey
                        ] || {}

                    // Update only lastProjection, preserve reference and forecast
                    this.$set(
                        this.forecast.scopes[this.scopeInEdition].dates,
                        dateKey,
                        {
                            ...currentDateEntry,
                            lastProjection: entryData,
                        }
                    )

                    // Update scope in array for reactivity
                    this.updateScopeInArray(this.scopeInEdition, newEntry.date)
                    return
                }

                // Normal flow: update forecast/reference locally
                this.createProjectionEntry(
                    this.scopeInEdition,
                    newEntry.date,
                    newEntry.amount,
                    newEntry.percentage
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        openHistory(scope) {
            this.historyQuote = scope
            this.historyView = true
        },
        closeHistoryView() {
            this.historyView = false
        },
        getTimestamp2(obj) {
            if (obj._seconds && obj._nanoseconds) {
                return obj._seconds * 1000 + obj._nanoseconds / 1e6
            } else if (obj.seconds && obj.nanoseconds) {
                return obj.seconds * 1000 + obj.nanoseconds / 1e6
            }
            return 0
        },
        toUtc5(timestamp) {
            const dateInUTC5 = new Date(timestamp - 5 * 60 * 60 * 1000)
            return dateInUTC5
        },
        convertForecastToBulkEntries() {
            const entries = []

            // Validate forecast exists
            if (!this.forecast || !this.forecast.scopes) {
                return entries
            }

            try {
                // Convert all forecast data to bulk format
                // Only include reference and forecast (not lastProjection)
                Object.entries(this.forecast.scopes).forEach(
                    ([scopeId, scope]) => {
                        // Validate scope exists and has dates
                        if (
                            scope &&
                            scope.dates &&
                            typeof scope.dates === 'object'
                        ) {
                            Object.entries(scope.dates).forEach(
                                ([dateKey, dateEntry]) => {
                                    // Validate dateEntry exists
                                    if (
                                        dateEntry &&
                                        typeof dateEntry === 'object'
                                    ) {
                                        // Handle reference entries
                                        if (
                                            dateEntry.reference &&
                                            dateEntry.reference !== 0 &&
                                            typeof dateEntry.reference ===
                                                'object'
                                        ) {
                                            entries.push({
                                                scopeId: scopeId,
                                                date: dateKey,
                                                amount:
                                                    parseFloat(
                                                        dateEntry.reference
                                                            .amount
                                                    ) || 0,
                                                percentage:
                                                    parseFloat(
                                                        dateEntry.reference
                                                            .percentage
                                                    ) || 0,
                                            })
                                        }

                                        // Handle forecast entries
                                        if (
                                            dateEntry.forecast &&
                                            dateEntry.forecast !== 0 &&
                                            typeof dateEntry.forecast ===
                                                'object'
                                        ) {
                                            entries.push({
                                                scopeId: scopeId,
                                                date: dateKey,
                                                amount:
                                                    parseFloat(
                                                        dateEntry.forecast
                                                            .amount
                                                    ) || 0,
                                                percentage:
                                                    parseFloat(
                                                        dateEntry.forecast
                                                            .percentage
                                                    ) || 0,
                                            })
                                        }
                                    }
                                }
                            )
                        }
                    }
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
            return entries
        },
        convertInitialProjectionToBulkEntries() {
            const entries = []

            // Validate forecast exists
            if (!this.forecast || !this.forecast.scopes) {
                return entries
            }

            try {
                // Convert only lastProjection entries
                Object.entries(this.forecast.scopes).forEach(
                    ([scopeId, scope]) => {
                        // Validate scope exists and has dates
                        if (
                            scope &&
                            scope.dates &&
                            typeof scope.dates === 'object'
                        ) {
                            Object.entries(scope.dates).forEach(
                                ([dateKey, dateEntry]) => {
                                    // Validate dateEntry exists and has lastProjection
                                    if (
                                        dateEntry &&
                                        typeof dateEntry === 'object' &&
                                        dateEntry.lastProjection &&
                                        dateEntry.lastProjection !== 0 &&
                                        typeof dateEntry.lastProjection ===
                                            'object'
                                    ) {
                                        entries.push({
                                            scopeId: scopeId,
                                            date: dateKey,
                                            amount:
                                                parseFloat(
                                                    dateEntry.lastProjection
                                                        .amount
                                                ) || 0,
                                            percentage:
                                                parseFloat(
                                                    dateEntry.lastProjection
                                                        .percentage
                                                ) || 0,
                                        })
                                    }
                                }
                            )
                        }
                    }
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
            return entries
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow-y: scroll;
}

.text-orange {
    color: orange;
    font-weight: bold;
}

.text-green {
    color: green;
    font-weight: bold;
}

.text-red {
    color: red;
    font-weight: bold;
}

.fixed-column {
    position: sticky;
    right: 0;
    z-index: 1;
}

.gray-background {
    background-color: #eee;
}

.white-background {
    background-color: white;
}

.dark-gray-background {
    background-color: #c3c3c3;
}

.v-data-table-header th {
    background-color: #eeeeee !important;
}

.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}

.v-data-table__row {
    height: 64px;
}

.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}

.v-data-table__wrapper {
    max-height: 65vh;
    overflow-y: auto;
}

thead {
    position: sticky;
    z-index: 10;
}
</style>
